import type { ReactNode } from "react";
import type {
  AuthorizationEvent,
  defaultAuthorizationServices,
} from "@/features/app-auth/authorizationMediator";
import {
  type AuthorizationPresentation,
  useAuthorizationPresenter,
} from "@/features/app-auth/useAuthorizationPresenter";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { Button } from "@/shared/ui/form";

interface AppAuthorizationPageProps {
  requestId: string;
  services?: typeof defaultAuthorizationServices;
}
export const AppAuthorizationPage: React.FunctionComponent<
  AppAuthorizationPageProps
> = ({ requestId, services }: AppAuthorizationPageProps) => {
  const presentation = useAuthorizationPresenter(requestId, services);
  return (
    <ComponentScope node={presentation.node}>
      <AppAuthorizationView
        {...presentation}
        onEvent={presentation.node.dispatch}
      />
    </ComponentScope>
  );
};

interface AppAuthorizationViewProps
  extends Omit<AuthorizationPresentation, "node"> {
  onEvent: (event: AuthorizationEvent) => void;
}
export function AppAuthorizationView({
  clientName,
  stage,
  message,
  onEvent,
}: AppAuthorizationViewProps): ReactNode {
  return (
    <section className="flex-1 overflow-y-auto" aria-label="アプリの利用許可">
      <div className="mx-auto max-w-xl space-y-6 px-4 py-8">
        <h1 className="text-xl font-bold">アプリの利用を許可</h1>
        {clientName !== null && (
          <p>
            <strong>{clientName}</strong>{" "}
            に、このブラウザと同じ利用者として掲示板を利用することを許可しますか？
          </p>
        )}
        <p>
          許可すると、アプリから閲覧・投稿・添付・そうだね・delができます。投稿削除には削除キーも必要です。利用制限は引き続き適用されます。資格は最長21日間有効です。
        </p>
        <div role="status" aria-live="polite">
          {stage === "loading" && "認証要求を確認しています…"}
          {stage === "checking" &&
            "ブラウザの安全確認と認証の完了を待っています…"}
          {stage === "ready" &&
            "安全確認が完了しました。アプリ名を確認して選択してください。"}
          {stage === "submitting" && "選択を送信しています…"}
          {(stage === "error" || stage === "complete") && message}
        </div>
        {stage !== "complete" && (
          <div className="flex flex-wrap gap-3">
            <Button
              variant="primary"
              disabled={stage !== "ready"}
              onClick={() =>
                onEvent({ type: "authorization.decide", decision: "approve" })
              }
            >
              許可する
            </Button>
            <Button
              disabled={clientName === null || stage === "submitting"}
              onClick={() =>
                onEvent({ type: "authorization.decide", decision: "deny" })
              }
            >
              拒否する
            </Button>
            <Button
              disabled={clientName === null || stage === "submitting"}
              onClick={() =>
                onEvent({ type: "authorization.decide", decision: "cancel" })
              }
            >
              キャンセル
            </Button>
            {stage === "error" && (
              <Button onClick={() => onEvent({ type: "authorization.retry" })}>
                再試行
              </Button>
            )}
          </div>
        )}
        {stage === "complete" && (
          <Button onClick={() => onEvent({ type: "authorization.return" })}>
            アプリへ戻る
          </Button>
        )}
        <p className="text-sm text-muted-foreground">
          アプリから開始していない場合は、この画面を閉じてください。認証情報やブラウザのCookieをアプリへコピーする必要はありません。
        </p>
      </div>
    </section>
  );
}
